import { faCheck, faDatabase, faDownload, faEnvelope, faTimes } from '@fortawesome/free-solid-svg-icons';
import { Action, DataTable, DataTableContainer, Filter, TableActionButton } from 'components/DataTable';
import { ActionCallbackProps, Column } from 'components/DataTable/DataTable';
import { MessageComponent } from 'components/MessageManagement/MessageComponent';
import { useModal } from 'hooks/useModal';
import moment from 'moment';
import dynamic from 'next/dynamic';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApplicationResponseAction, ApplicationResponseForm } from '.';
import { useApi } from '../../../hooks/useApi';
import useFileDownload from '../../../hooks/useFileDownload';
import { Application } from '../types';
import ApplicationStatusBadge from './ApplicationStatusBadge';

const FormDataPreview = dynamic(() => import('../../Formular/FormDataPreview'));

interface Props {
    clubSlug: string;
}

const ApplicationList = ({ clubSlug }: Props) => {
    const { t } = useTranslation('member-management');
    const api = useApi();
    const [setModal, resetModal] = useModal();
    const [isLoading, setIsLoading] = useState(false);
    const download = useFileDownload();
    const listRef = useRef(null);

    const columns: Column[] = useMemo(
        () => [
            {
                name: t('applications.list.columns.name'),
                uid: 'user_name',
                allowsSorting: true
            },
            {
                name: t('applications.list.columns.email'),
                uid: 'user_email',
                allowsSorting: true
            },
            {
                name: t('applications.list.columns.status'),
                uid: 'status',
                allowsSorting: true,
                renderer: (value) => <ApplicationStatusBadge status={value} />
            },
            {
                name: t('applications.list.columns.createdAt'),
                uid: 'created_at',
                allowsSorting: true,
                renderer: (value) => (value ? moment(value).format('DD.MM.YYYY, HH:mm') : '')
            },
            {
                name: t('applications.list.columns.updatedAt'),
                uid: 'updated_at',
                allowsSorting: true,
                renderer: (value) => (value ? moment(value).format('DD.MM.YYYY, HH:mm') : '')
            },
            {
                name: t('applications.list.columns.actions'),
                uid: 'actions',
                align: 'end'
            }
        ],
        [t]
    );

    const showApplicationResponseForm = (application: Application, action: ApplicationResponseAction) => {
        setModal({
            title:
                action === 'accept'
                    ? t('applications.list.acceptModalTitle', { name: application.user_name })
                    : t('applications.list.declineModalTitle', { name: application.user_name }),
            content: (
                <ApplicationResponseForm
                    clubSlug={clubSlug}
                    action={action}
                    application={application}
                    callback={() => {
                        listRef?.current?.reload();
                        resetModal();
                    }}
                />
            ),
            show: true
        });
    };

    const showFormDataPreview = (application: Application) => {
        setModal({
            title: t('applications.list.formDataModalTitle', { name: application.user_name }),
            content: <FormDataPreview formDataId={application.form_data_id} />,
            show: true,
            size: 60
        });
    };

    const showMessageComponentHandler = (application: Application) => {
        setModal({
            title: t('applications.list.messageModalTitle', { name: application.user_name }),
            content: (
                <MessageComponent addresses={[application.user_email]} clubSlug={clubSlug} callback={resetModal} />
            ),
            show: true
        });
    };

    const filters: Filter[] = useMemo(
        () => [
            {
                filterName: t('applications.list.filters.formData'),
                filterType: 'text',
                filterKey: 'form'
            },
            {
                filterName: t('applications.list.filters.status'),
                filterKey: 'status',
                filterType: 'select',
                options: [
                    { label: t('applications.list.filters.statusRequested'), value: 'requested' },
                    { label: t('applications.list.filters.statusAccepted'), value: 'accepted' },
                    { label: t('applications.list.filters.statusDeclined'), value: 'declined' }
                ]
            }
        ],
        [t]
    );

    const handleDownloadFormData = useCallback(
        ({ selection, filter }: ActionCallbackProps) => {
            setIsLoading(true);
            void api({
                url: `club-member/${clubSlug}/application/download`,
                method: 'POST',
                data: { selection, filter },
                onSuccess: (data) => download(data.download_id),
                onBefore: () => setIsLoading(false)
            });
        },
        [clubSlug]
    );

    const actions: Action[] = useMemo(
        () => [
            {
                key: 'download',
                label: t('applications.list.downloadSelected'),
                action: handleDownloadFormData,
                icon: faDownload
            }
        ],
        [t, handleDownloadFormData]
    );
    return (
        <DataTableContainer>
            <DataTable
                asyncListRef={listRef}
                fetchUrl={`club-member/${clubSlug}/application`}
                columns={columns}
                filters={filters}
                actions={actions}
                actionsLoading={isLoading}
                tableProps={{
                    selectionMode: 'multiple',
                    sticked: true,
                    bordered: true,
                    borderWeight: 'light',
                    compact: false
                }}
                searchInputProps={{ placeholder: t('applications.list.searchPlaceholder') }}
                rowActions={(item) => (
                    <TableActionButton.Container justify="flex-end">
                        {item.status === 'requested' && (
                            <>
                                <TableActionButton
                                    icon={faCheck}
                                    tooltip={t('applications.list.acceptTooltip')}
                                    onPress={() => showApplicationResponseForm(item, 'accept')}
                                />
                                <TableActionButton
                                    icon={faTimes}
                                    color="error"
                                    tooltip={t('applications.list.declineTooltip')}
                                    onPress={() => showApplicationResponseForm(item, 'decline')}
                                />
                            </>
                        )}
                        <TableActionButton
                            icon={faEnvelope}
                            tooltip={t('applications.list.sendMessageTooltip')}
                            color="light"
                            onPress={() => showMessageComponentHandler(item)}
                        />

                        {item.form_data_id && (
                            <TableActionButton
                                icon={faDatabase}
                                color="light"
                                tooltip={t('applications.list.relatedDataTooltip')}
                                onPress={() => showFormDataPreview(item)}
                            />
                        )}
                    </TableActionButton.Container>
                )}
            />
        </DataTableContainer>
    );
};

export default ApplicationList;
